/**
 * ENHANCED SKY, the drawn half: one dome, one material, one draw call, and the
 * only place in this port where a colour is allowed to be brighter than white.
 *
 * `enhanced-sky-look.ts` decides *which* `BARON.PAL` entries a mission's hour
 * and sky word are painted from; this file turns them into a hemisphere and
 * adds the sun's own light to it.
 *
 * ## The HDR boundary, exactly
 *
 * `three/webgpu-support.ts` turns three's colour management **off** app-wide so
 * a `BARON.PAL` byte divided by 255 reaches the framebuffer untouched. That
 * covenant is what makes every colour census in this repo meaningful, and a
 * scene-wide `renderer.toneMapping` or a full-screen bloom pass would end it
 * for every material at once — the aeroplanes, the terrain, the cockpit and the
 * HUD included.
 *
 * So the high dynamic range here is **scoped to this one fragment shader**:
 *
 * - **Inside** `skyRadiance()` the units are radiance, `1.0` is display white,
 *   and the sun's halo reaches roughly `5` of it. Nothing outside this function
 *   ever sees a value above 1.
 * - **The last three operations** of the shader are `toneMap`, then
 *   `quantiseToVgaDac`, then output. What leaves is in `[0, 1]`, on the 6-bit
 *   grid the palette itself lives on.
 * - **No render target, no post-processing pass, no renderer state changes.**
 *   The framebuffer contract for every other object in the scene is byte-for-
 *   byte what it was, and with `ENHANCED SKY` off this file builds nothing at
 *   all.
 *
 * `toneMap` is deliberately **identity below the knee**. A palette entry
 * darker than `HDR_KNEE` comes out of the shader as itself, so the measured
 * `#7db6c3` daytime sky is still `#7db6c3` away from the sun — which is the
 * property `docs/formats/world-terrain.md` §7.1's measurement can be re-run
 * against. Only the handful of stops above the knee (the sunset ramp's
 * brightest entries) roll off, and rolling off is what they are there for.
 *
 * Per-channel rather than on luminance, which whitens a colour as it saturates:
 * red clips before green before blue, so a bright orange walks toward yellow
 * and then toward white. That is not a modern film curve borrowed from
 * elsewhere — it is the shape of `BARON.PAL`'s own fire ramp, 216 `#e70000` ->
 * 211 `#fbfb00` -> 209 `#ffffe7`, which is the original artists' own statement
 * about what a bright thing looks like.
 *
 * ## Why the glare is sky and not a sprite
 *
 * The obvious build is a camera-facing additive quad at the sun's bearing. This
 * is not that, for three reasons and one measurement:
 *
 * 1. **Occlusion comes free.** The dome writes no depth and draws first, so
 *    every opaque thing in the scene — an aeroplane, a hill, a cloud slab —
 *    paints over the halo without anything being asked to. An additive quad
 *    would need a depth test to get the same result, and would still be wrong
 *    against the cloud that `clouds.c` uses to hide the sun outright.
 * 2. **It costs no draw call.** The halo is four `pow`s inside a shader that
 *    was already going to run over those pixels.
 * 3. **`transparent: true` + `depthWrite: false` is a measured hazard on this
 *    renderer.** The 2026-08-22 bullet lane found that pair rasterising *zero*
 *    pixels under reversed-Z on a pooled mesh (`lessons.md`). The sky path has
 *    no reason to go near it.
 *
 * What this deliberately does not do is spill glare *over* the aeroplane in
 * front of the sun. That is a screen-space effect and it needs a real bloom
 * pass; `docs/engineering/enhanced-sky.md` records why that pass was costed and
 * declined.
 */

import * as THREE from "three/webgpu";
import type { Vec3 } from "@red-opus/engine";
import {
  blendExponentForElevation,
  greyedStops,
  horizonWarmthForSun,
  lobeExponentForHalfAngle,
  streakGainForSun,
  type EnhancedSkyLook,
  type EnhancedSkyStops,
  GLARE_CORE_HALF_ANGLE_DEG,
  GLARE_WASH_HALF_ANGLE_DEG,
  STREAK_ELEVATION_FALLOFF,
  STREAK_HALF_ANGLE_DEG,
} from "./enhanced-sky-look.ts";
import type { PaletteColour } from "./time-of-day.ts";
import { quantiseToVgaDac } from "../three/ordered-dither.ts";
import { skyDome, SKY_RADIUS_M } from "./world.ts";

/**
 * Where the tone curve stops being the identity, in units of display white.
 * `0.75` is 191/255 — above every gradient stop this port draws except the
 * sunset ramp's top three, and far enough below 1 that there is a shoulder to
 * roll a halo off along.
 */
export const HDR_KNEE = 0.75;

/**
 * `dir.xz` is degenerate at the poles and `normalize` of it is a NaN that
 * survives being multiplied by zero. Divided by a floored length instead.
 */
const MIN_HORIZONTAL_LENGTH = 1e-4;

type Vec3Node = THREE.Node<"vec3">;
type FloatNode = ReturnType<typeof THREE.TSL.dot>;

/** A palette entry as the shader wants it — see `world.ts`'s note on colour management. */
function paletteVec3(colour: PaletteColour): Vec3Node {
  return THREE.TSL.vec3(((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255);
}

/**
 * `x` below `HDR_KNEE`, and an exponential shoulder onto `1` above it.
 *
 * `k + (1 - k)(1 - e^-(x-k)/(1-k))`. Its derivative at the knee is exactly 1
 * from both sides, so there is no visible seam where the curve starts working,
 * and it asymptotes to 1 rather than reaching it — a radiance of 5 comes out at
 * 0.9985, i.e. 254/255, which is why a sun halo goes white without clipping to
 * a hard-edged disc of pure `#ffffff`.
 */
export function toneMap(radiance: Vec3Node): Vec3Node {
  const shoulder = 1 - HDR_KNEE;
  const over = THREE.TSL.max(radiance.sub(HDR_KNEE), 0);
  const rolled = THREE.TSL.exp(over.div(shoulder).negate()).oneMinus().mul(shoulder);
  return THREE.TSL.min(radiance, HDR_KNEE).add(rolled);
}

/** The same curve in plain arithmetic, so `enhanced-sky-tone.test.ts` can pin it without a GPU. */
export function toneMapChannel(radiance: number): number {
  const shoulder = 1 - HDR_KNEE;
  const over = Math.max(radiance - HDR_KNEE, 0);
  return Math.min(radiance, HDR_KNEE) + shoulder * (1 - Math.exp(-over / shoulder));
}

/** Unit direction from the dome's centre to this fragment — the dome is a sphere about the camera. */
function fragmentDirection(): Vec3Node {
  return THREE.TSL.normalize(THREE.TSL.positionGeometry);
}

/** `cos` of the compass angle between this fragment's bearing and the sun's, clamped to the near half. */
function azimuthalCloseness(direction: Vec3Node, sun: Vec3): FloatNode {
  const horizontalLength = Math.hypot(sun.x, sun.z);
  const sunAzimuth = THREE.TSL.vec2(
    sun.x / Math.max(horizontalLength, MIN_HORIZONTAL_LENGTH),
    sun.z / Math.max(horizontalLength, MIN_HORIZONTAL_LENGTH),
  );
  const flat = THREE.TSL.vec2(direction.x, direction.z);
  const bearing = flat.div(THREE.TSL.max(THREE.TSL.length(flat), MIN_HORIZONTAL_LENGTH));
  return THREE.TSL.dot(bearing, sunAzimuth).clamp(0, 1);
}

/**
 * The three-stop gradient: mid through the middle, `zenith` overhead,
 * `horizonCool`/`horizonWarm` at the skyline picked by bearing.
 *
 * Both blends are `pow` of a sine of elevation, with the exponent solved back
 * from a stated angle (`blendExponentForElevation`) so the numbers in
 * `enhanced-sky-look.ts` are ones a viewer can check against the screen.
 */
function gradient(stops: EnhancedSkyStops, direction: Vec3Node, warm: FloatNode): Vec3Node {
  const up = direction.y.clamp(0, 1);
  const towardsZenith = THREE.TSL.pow(up, blendExponentForElevation(stops.midElevationDeg, false));
  const towardsHorizon = THREE.TSL.pow(up.oneMinus(), blendExponentForElevation(stops.hazeElevationDeg, true));
  const horizon = THREE.TSL.mix(paletteVec3(stops.horizonCool), paletteVec3(stops.horizonWarm), warm);
  const lower = THREE.TSL.mix(paletteVec3(stops.mid), horizon, towardsHorizon);
  return THREE.TSL.mix(lower, paletteVec3(stops.zenith), towardsZenith);
}

/**
 * Everything the sun adds, in radiance: a tight halo on the disc, a broad wash
 * around it, and — only while the sun is within `STREAK_MAX_SUN_ELEVATION_DEG`
 * of the horizon — a band smeared sideways along the skyline.
 *
 * The streak is anisotropic by construction rather than by squashing a circle:
 * `STREAK_HALF_ANGLE_DEG` sets how far it reaches in *bearing* and
 * `STREAK_ELEVATION_FALLOFF` how fast it dies in *elevation*, which is the
 * shape a low sun actually makes and needs no second lobe to fake it.
 */
function glare(stops: EnhancedSkyStops, direction: Vec3Node, sun: Vec3, azimuth: FloatNode, scale: number): Vec3Node {
  const towardsSun = THREE.TSL.dot(direction, THREE.TSL.vec3(sun.x, sun.y, sun.z)).clamp(0, 1);
  const core = THREE.TSL.pow(towardsSun, lobeExponentForHalfAngle(GLARE_CORE_HALF_ANGLE_DEG));
  const wash = THREE.TSL.pow(towardsSun, lobeExponentForHalfAngle(GLARE_WASH_HALF_ANGLE_DEG));
  const streakGain = streakGainForSun(sun.y) * scale;
  const verticalGap = direction.y.sub(sun.y).abs();
  const streak = THREE.TSL.pow(azimuth, lobeExponentForHalfAngle(STREAK_HALF_ANGLE_DEG))
    .mul(THREE.TSL.exp(verticalGap.mul(-STREAK_ELEVATION_FALLOFF)))
    .mul(streakGain);
  return paletteVec3(stops.glareCore)
    .mul(core.mul(stops.glareCoreGain * scale))
    .add(paletteVec3(stops.glareWash).mul(wash.mul(stops.glareWashGain * scale).add(streak)));
}

/**
 * The whole shader, as one expression: gradient plus glare in radiance, tone
 * curve, DAC grid. Exported so a test can assert the order of those last two —
 * quantising before tone-mapping would put the grid in the wrong space and
 * band the shoulder, which is exactly the kind of thing that looks fine in a
 * still and crawls in motion.
 */
export function skyRadiance(look: EnhancedSkyLook, sun: Vec3): Vec3Node {
  const stops = greyedStops(look);
  const direction = fragmentDirection();
  const azimuth = azimuthalCloseness(direction, sun);
  // Scaled by the sun's own height, not just by bearing: see
  // `horizonWarmthForSun`, which exists because the first noon capture of this
  // dome grew an orange band along the skyline at midday.
  const warm = THREE.TSL.pow(azimuth, lobeExponentForHalfAngle(stops.warmthSpreadDeg)).mul(horizonWarmthForSun(sun.y));
  const radiance = gradient(stops, direction, warm).add(
    glare(stops, direction, sun, azimuth, look.treatment.glareScale),
  );
  return quantiseToVgaDac(toneMap(radiance));
}

/**
 * The enhanced dome. `sun` is the mission's sky-object bearing — the sun's
 * while it is up, the **moon's** once `sun.c`'s own `sin(angle) < 0` test has
 * put the sun away, so night's halo hangs on the object that is actually there.
 * At night under this treatment that is `night-sky-look.ts`'s *real* moon, at
 * the bearing the ephemeris puts it for the mission's date and region, so the
 * halo and the disc cannot end up in two different places; it is passed even
 * when the moon is below the horizon, where every glare term goes to zero on
 * its own (`dot(direction, sun).clamp(0, 1)`). `undefined` for a sortie that
 * briefs no weather, which gets the gradient and no glare at all.
 */
export function createEnhancedSky(look: EnhancedSkyLook, sun: Vec3 | undefined): THREE.Mesh {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide, fog: false, depthWrite: false });
  material.colorNode = skyRadiance(look, sun ?? OVERHEAD);
  return skyDome(material);
}

/** Stand-in bearing for a sortie with no hour: straight up, where a noon sun would be. */
const OVERHEAD: Vec3 = { x: 0, y: 1, z: 0 };

/** Re-exported so callers building a dome do not have to reach into `world.ts` for the radius as well. */
export { SKY_RADIUS_M };
